import { CabinOverridesPayload, SeatConfiguration, SeatLayoutEntry, SeatRowSelection } from '../charter.types';

type OrderedFacilityRow = { layout: Array<SeatLayoutEntry>; overrideOrder: number };

type ResolvedRowLayouts = {
  primaryLayout: Array<SeatLayoutEntry>;
  facilityRowsBefore: Array<OrderedFacilityRow>;
  facilityRowsAfter: Array<OrderedFacilityRow>;
};

type FloatingFacilityRow = {
  rowNumber: number;
  layout: Array<SeatLayoutEntry>;
  overrideOrder: number;
};

const expandRowSelection = (selection: SeatRowSelection): Array<number> => {
  if (Array.isArray(selection)) {
    return selection;
  }

  const rows: Array<number> = [];
  for (let row = selection.start; row <= selection.end; row += 1) {
    rows.push(row);
  }

  return rows;
};

export const resolveRowLayouts = (
  baseLayout: Array<SeatLayoutEntry>,
  cabinOverrides: Array<CabinOverridesPayload['overrides'][number]>,
  rowNumber: number,
): ResolvedRowLayouts => {
  const facilityRowsBefore: Array<OrderedFacilityRow> = [];
  const facilityRowsAfter: Array<OrderedFacilityRow> = [];
  let layout = baseLayout;

  cabinOverrides.forEach((override, overrideIndex) => {
    if (!override.seatLayout) {
      return;
    }

    const rows = expandRowSelection(override.rows);

    if (!rows.includes(rowNumber)) {
      return;
    }

    const hasSeatEntries = override.seatLayout.some((entry) => entry.kind === 'seat');

    if (hasSeatEntries) {
      layout = override.seatLayout;
      return;
    }

    const targetCollection =
      (override.facilityRowPosition ?? 'before') === 'after' ? facilityRowsAfter : facilityRowsBefore;

    targetCollection.push({ layout: override.seatLayout, overrideOrder: overrideIndex });
  });

  return {
    primaryLayout: layout,
    facilityRowsBefore,
    facilityRowsAfter,
  };
};

export const collectFloatingFacilityRows = (
  cabinOverrides: Array<CabinOverridesPayload['overrides'][number]>,
  rowRange: SeatConfiguration['cabins'][number]['rowRange'],
): Array<FloatingFacilityRow> => {
  const floatingRows: Array<FloatingFacilityRow> = [];

  cabinOverrides.forEach((override, overrideIndex) => {
    if (!override.seatLayout) {
      return;
    }

    const hasSeatEntries = override.seatLayout.some((entry) => entry.kind === 'seat');

    if (hasSeatEntries) {
      return;
    }

    const rows = expandRowSelection(override.rows);

    rows.forEach((rowNumber) => {
      const withinBaseRange = Number.isInteger(rowNumber) && rowNumber >= rowRange.start && rowNumber <= rowRange.end;

      if (withinBaseRange) {
        return;
      }

      floatingRows.push({
        rowNumber,
        layout: override.seatLayout,
        overrideOrder: overrideIndex,
      });
    });
  });

  return floatingRows;
};

export const resolveSeatOverrides = (
  cabinOverrides: Array<CabinOverridesPayload['overrides'][number]>,
  rowNumber: number,
  seatLetter: string,
): Array<CabinOverridesPayload['overrides'][number]> =>
  cabinOverrides.filter((override) => {
    const rows = expandRowSelection(override.rows);
    const rowMatches = rows.includes(rowNumber);
    const seatMatches = !override.seatLetters || override.seatLetters.includes(seatLetter);

    return rowMatches && seatMatches;
  });
